/**
 * Timeline Screen Component
 *
 * Displays a scrollable history of accomplishments grouped by date.
 * Includes filtering by category and search functionality.
 */

import { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  X,
  Calendar,
  Loader2,
  Plus,
} from 'lucide-react';
import {
  useEntriesByDateRange,
  useSearchEntries,
  CATEGORIES,
  CATEGORY_META,
  formatRelativeDate,
  getToday,
  type Category,
  type Entry,
} from '@have-done/shared';
import { EntryCard, EntryListEmpty } from './EntryCard';
import { EditEntryModal } from './EditEntryModal';
import { AddEntryModal } from './AddEntryModal';

/**
 * Get date range for the past N days
 */
function getDateRange(days: number): { startDate: string; endDate: string } {
  const endDate = getToday();
  const start = new Date();
  start.setDate(start.getDate() - days + 1);
  const startDate = start.toISOString().split('T')[0];
  return { startDate, endDate };
}

/**
 * Group entries by date
 */
function groupEntriesByDate(entries: Entry[]): Map<string, Entry[]> {
  const grouped = new Map<string, Entry[]>();

  // Sort entries by date (newest first) and creation time
  const sorted = [...entries].sort((a, b) => {
    if (a.date !== b.date) {
      return b.date.localeCompare(a.date);
    }
    return b.createdAt - a.createdAt;
  });

  for (const entry of sorted) {
    const existing = grouped.get(entry.date) || [];
    grouped.set(entry.date, [...existing, entry]);
  }

  return grouped;
}

const DATE_RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

const DEFAULT_DATE_RANGE = 14;

export function TimelineScreen() {
  const [selectedRange, setSelectedRange] = useState(DEFAULT_DATE_RANGE);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [addingForDate, setAddingForDate] = useState<string | null>(null);

  const { startDate, endDate } = getDateRange(selectedRange);

  // Check if any filters are active
  const hasActiveFilters = searchQuery.length > 0 || selectedCategory !== null || selectedRange !== DEFAULT_DATE_RANGE;

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setSelectedRange(DEFAULT_DATE_RANGE);
  };

  // Fetch entries based on search or date range
  const rangeEntries = useEntriesByDateRange(startDate, endDate);
  const searchResults = useSearchEntries(searchQuery, 50);

  // Use search results if searching, otherwise use date range
  const isSearching = searchQuery.length > 0;
  const isSearchLoading = isSearching && searchResults === undefined;
  const rawEntries = isSearching ? searchResults : rangeEntries;

  // Apply category filter
  const filteredEntries = useMemo((): Entry[] => {
    if (!rawEntries) return [];
    const entries = rawEntries as Entry[];
    if (!selectedCategory) return entries;
    return entries.filter((entry: Entry) => entry.category === selectedCategory);
  }, [rawEntries, selectedCategory]);

  // Group by date for display
  const groupedEntries = useMemo(
    () => groupEntriesByDate(filteredEntries),
    [filteredEntries]
  );

  // Loading state - skeleton mirrors actual layout structure exactly
  if (rawEntries === undefined) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header skeleton - matches actual header structure */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="h-8 w-28 bg-bg-tertiary rounded" />
            <div className="h-5 w-36 bg-bg-tertiary rounded mt-1" />
          </div>
        </header>

        {/* Search and filters skeleton */}
        <div className="space-y-3">
          {/* Search bar skeleton */}
          <div className="relative">
            <div className="w-full h-[42px] bg-bg-secondary border border-border-light rounded-lg" />
          </div>

          {/* Filter controls skeleton */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="h-[34px] w-20 bg-bg-secondary rounded-lg" />
            <div className="h-[34px] w-32 bg-bg-secondary rounded-lg" />
          </div>
        </div>

        {/* Timeline skeleton - date groups with entries */}
        <div className="space-y-6">
          {[1, 2].map((group) => (
            <section key={group}>
              {/* Date header skeleton */}
              <div className="flex items-center justify-between mb-3 py-2">
                <div className="h-4 w-24 bg-bg-tertiary rounded" />
                <div className="h-6 w-12 bg-bg-tertiary rounded" />
              </div>
              {/* Entry cards skeleton */}
              <div className="space-y-3">
                {[1, 2].map((entry) => (
                  <div key={entry} className="rounded-lg border-l-4 border-bg-tertiary bg-bg-secondary p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="h-5 w-20 bg-bg-tertiary rounded-full" />
                      <div className="h-6 w-6 bg-bg-tertiary rounded" />
                    </div>
                    <div className="h-5 w-3/4 bg-bg-tertiary rounded" />
                    <div className="h-4 w-1/2 bg-bg-tertiary rounded mt-2" />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  const totalEntries = filteredEntries.length;
  const dateGroups = Array.from(groupedEntries.entries());

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-text-primary">
            Timeline
          </h2>
          <p className="text-text-secondary">
            {totalEntries} accomplishment{totalEntries !== 1 ? 's' : ''}
            {selectedCategory && ` in ${CATEGORY_META[selectedCategory].label}`}
          </p>
        </div>
      </header>

      {/* Search and filters */}
      <div className="space-y-3">
        {/* Search bar */}
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search accomplishments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full pl-10 pr-10 py-2.5 rounded-lg
              bg-bg-secondary border border-border-light
              text-text-primary placeholder:text-text-tertiary
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
              transition-colors
            "
          />
          {searchQuery && (
            isSearchLoading ? (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 text-text-tertiary animate-spin" aria-label="Searching..." />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-text-tertiary hover:text-text-secondary"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )
          )}
        </div>

        {/* Filter controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle filters */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
              text-sm font-medium transition-colors
              ${showFilters
                ? 'bg-primary text-white'
                : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary'}
            `}
          >
            <Filter className="h-4 w-4" />
            Filters
            {selectedCategory && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">1</span>
            )}
          </button>

          {/* Date range selector */}
          {!isSearching && (
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
              <select
                value={selectedRange}
                onChange={(e) => setSelectedRange(Number(e.target.value))}
                className="
                  bg-bg-secondary border border-border-light rounded-lg
                  px-2 py-1.5 text-sm text-text-secondary
                  focus:outline-none focus:ring-2 focus:ring-primary/50
                "
              >
                {DATE_RANGE_OPTIONS.map((option) => (
                  <option key={option.days} value={option.days}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Active category filter badge */}
          {selectedCategory && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-sm text-primary">
              {CATEGORY_META[selectedCategory].label}
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className="p-0.5 rounded-full hover:bg-primary/20"
                aria-label={`Remove ${CATEGORY_META[selectedCategory].label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          {/* Clear all filters button */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto text-sm text-text-tertiary hover:text-text-secondary transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="p-4 rounded-lg bg-bg-secondary border border-border-light">
            <p className="text-sm font-medium text-text-secondary mb-2">Category</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                  ${!selectedCategory
                    ? 'bg-primary text-white'
                    : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/80'}
                `}
              >
                All
              </button>
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                    ${selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/80'}
                  `}
                >
                  {CATEGORY_META[category].label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      {dateGroups.length > 0 ? (
        <div className="space-y-6">
          {dateGroups.map(([date, entries]) => (
            <section key={date}>
              {/* Date header with add button */}
              <div className="flex items-center justify-between mb-3 sticky top-16 bg-bg-primary/95 backdrop-blur py-2 -mx-1 px-1">
                <h3 className="text-sm font-medium text-text-secondary">
                  {formatRelativeDate(date)}
                </h3>
                <button
                  type="button"
                  onClick={() => setAddingForDate(date)}
                  className="
                    inline-flex items-center gap-1 px-2 py-1 rounded-md
                    text-xs font-medium text-text-tertiary
                    hover:text-text-secondary hover:bg-bg-secondary
                    transition-colors
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                  "
                  aria-label={`Add accomplishment for ${formatRelativeDate(date)}`}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="hidden sm:inline">Add</span>
                </button>
              </div>
              {/* Entries for this date */}
              <div className="space-y-3">
                {entries.map((entry) => (
                  <EntryCard
                    key={entry._id}
                    entry={entry}
                    onEdit={setEditingEntry}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <EntryListEmpty
          message={
            isSearching
              ? `No results for "${searchQuery}"`
              : selectedCategory
              ? `No ${CATEGORY_META[selectedCategory].label.toLowerCase()} accomplishments`
              : 'No accomplishments in this time period'
          }
        />
      )}

      {/* Edit modal */}
      <EditEntryModal
        entry={editingEntry}
        isOpen={editingEntry !== null}
        onClose={() => setEditingEntry(null)}
      />

      {/* Add entry modal for specific date */}
      <AddEntryModal
        isOpen={addingForDate !== null}
        onClose={() => setAddingForDate(null)}
        initialDate={addingForDate ?? undefined}
      />
    </div>
  );
}
